import { useEffect } from 'react';
import { Control, Controller, FieldErrors, UseFormSetValue, useController, useWatch } from 'react-hook-form';
import { Checkbox, Col, Divider, Form, Input, Row, Select, Typography } from 'antd';
import { TYPE_CONFIG, TYPE_OPTIONS } from '../shared/lestnicyTypeConfig';

export const PRICE_ON_REQUEST = 'уточняйте';

export type ProductPayload = {
  subcategory: string;
  type: string;
  title: string;
  brand: string;
  article: string;
  weight: string;
  steps: string;
  price: string;
  image: string;
  sizes: string[];
};

export const EMPTY_PRODUCT: ProductPayload = {
  subcategory: '',
  type: '',
  title: '',
  brand: '',
  article: '',
  weight: '',
  steps: '',
  price: '',
  image: '',
  sizes: [],
};

type ProductFormProps = {
  control: Control<ProductPayload>;
  errors: FieldErrors<ProductPayload>;
  categoryOptions: Array<{ value: string; label: string }>;
  setValue: UseFormSetValue<ProductPayload>;
};

function ProductForm({ control, errors, categoryOptions, setValue }: ProductFormProps) {
  const { field: priceField } = useController({
    name: 'price',
    control,
    rules: {
      required: 'Введите цену.',
      validate: (v) => {
        if (v === PRICE_ON_REQUEST) return true;
        if (v.trim() === '') return 'Введите цену.';
        if (!/^\d+([.,]\d+)?$/.test(v.trim()))
          return 'Только цифры, одна точка или запятая (например: 95,19).';
        return true;
      },
    },
  });

  const isPriceOnRequest = priceField.value === PRICE_ON_REQUEST;

  const selectedType = useWatch({ control, name: 'type' });
  const selectedTypeConfig = TYPE_CONFIG[selectedType] ?? null;
  const sizeLabels = selectedTypeConfig?.sizes ?? [];
  const typeOptions = TYPE_OPTIONS.map(({ value, label }) => ({
    value,
    label: (
      <div className="admin-type-option-content">
        <img
          src={TYPE_CONFIG[value]?.image}
          alt={label}
          style={{ width: 'auto', height: 24, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
        />
        <span className="admin-type-option-text">{label}</span>
      </div>
    ),
  }));

  useEffect(() => {
    setValue('sizes', []);
  }, [selectedType, setValue]);

  return (
    <Form layout="vertical">
      <Form.Item
        label="Подкатегория"
        required
        validateStatus={errors.subcategory ? 'error' : ''}
        help={errors.subcategory?.message}
      >
        <Controller
          name="subcategory"
          control={control}
          rules={{ required: 'Выберите подкатегорию.' }}
          render={({ field }) => <Select {...field} options={categoryOptions} />}
        />
      </Form.Item>

      <Form.Item
        label="Название"
        required
        validateStatus={errors.title ? 'error' : ''}
        help={errors.title?.message}
      >
        <Controller
          name="title"
          control={control}
          rules={{
            required: 'Введите название товара.',
            validate: (v) => v.trim() !== '' || 'Введите название товара.',
          }}
          render={({ field }) => (
            <Input {...field} placeholder="Например: Лестница алюминиевая" />
          )}
        />
      </Form.Item>

      <Form.Item
        label="Артикул"
        required
        validateStatus={errors.article ? 'error' : ''}
        help={errors.article?.message}
      >
        <Controller
          name="article"
          control={control}
          rules={{
            required: 'Введите артикул.',
            validate: (v) => v.trim() !== '' || 'Введите артикул.',
          }}
          render={({ field }) => <Input {...field} placeholder="Например: KR-12345" />}
        />
      </Form.Item>

      <Form.Item
        label="Цена (BYN)"
        required
        validateStatus={errors.price ? 'error' : ''}
        help={errors.price?.message}
      >
        <Input
          {...priceField}
          disabled={isPriceOnRequest}
          placeholder={isPriceOnRequest ? 'Уточняйте' : 'Например: 95,19'}
          value={isPriceOnRequest ? '' : priceField.value}
          style={{ marginBottom: 8 }}
        />
        <Checkbox
          checked={isPriceOnRequest}
          onChange={(e) => priceField.onChange(e.target.checked ? PRICE_ON_REQUEST : '')}
        >
          Цена по запросу
        </Checkbox>
      </Form.Item>

      <Form.Item
        label="Ссылка на изображение"
        required
        validateStatus={errors.image ? 'error' : ''}
        help={errors.image?.message}
      >
        <Controller
          name="image"
          control={control}
          rules={{
            required: 'Укажите ссылку на изображение.',
            validate: (v) => v.trim() !== '' || 'Укажите ссылку на изображение.',
          }}
          render={({ field }) => (
            <Input {...field} placeholder="https://example.com/image.jpg" />
          )}
        />
      </Form.Item>

      <Form.Item label="Бренд">
        <Controller
          name="brand"
          control={control}
          render={({ field }) => <Input {...field} placeholder="Например: Krause" />}
        />
      </Form.Item>

      <Form.Item label="Вес (кг)">
        <Controller
          name="weight"
          control={control}
          render={({ field }) => <Input {...field} placeholder="Например: 5.2" />}
        />
      </Form.Item>

      <Form.Item label="Ступени">
        <Controller
          name="steps"
          control={control}
          render={({ field }) => <Input {...field} placeholder="Например: 8" />}
        />
      </Form.Item>

      <Form.Item
        label="Вариант размера"
        help="Определяет схему и поля размеров"
      >
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              value={field.value || undefined}
              className="admin-type-select"
              popupClassName="admin-type-select-dropdown"
              allowClear
              placeholder="Выберите вариант размера"
              options={typeOptions}
            />
          )}
        />
      </Form.Item>

      {sizeLabels.length > 0 && (
        <>
          <Divider style={{ margin: '8px 0 12px' }}>Размеры</Divider>
          {selectedTypeConfig?.image && (
            <div style={{ marginBottom: 12 }}>
              <Typography.Text type="secondary">Схема размеров</Typography.Text>
              <img
                src={selectedTypeConfig.image}
                alt="Схема размеров"
                style={{ width: '100%', marginTop: 8, borderRadius: 8 }}
              />
            </div>
          )}
          <Row gutter={[12, 0]}>
            {sizeLabels.map(({ label, unit }, index) => (
              <Col key={label} span={6}>
                <Form.Item label={`${label}, ${unit}`} style={{ marginBottom: 8 }}>
                  <Controller
                    name={`sizes.${index}` as any}
                    control={control}
                    render={({ field }) => (
                      <Input {...field} placeholder="0.00" />
                    )}
                  />
                </Form.Item>
              </Col>
            ))}
          </Row>
        </>
      )}
    </Form>
  );
}

export default ProductForm;
